import React, { useState, useEffect, useRef } from "react";
import Card from "@amzn/meridian/esm/components/card";
import Column from "@amzn/meridian/esm/components/column";
import Link from "@amzn/meridian/esm/components/link";
import RadioButton from "@amzn/meridian/esm/components/radio-button";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import Toggle from "@amzn/meridian/esm/components/toggle";

import InfoCard, { INFO_CARD_VARIANT } from "../../../components/InfoCard";
import { FEEDBACK_PAGE } from "../../../constants";
import { VERBOSITY_LEVELS, PREFERENCE_SAVE_ERROR_MESSAGE } from "../../../constants";
import { THEME_COLOR } from "../../../styles/colors";
import LogManager from "../../../utils/logManager";
import { openUrl } from "../../../utils/zoomSdkUtils";
import { getVerbosityLevel, updateVerbosityLevel, getAutoStart, updateAutoStart } from "./settingsTab.utils";
import ErrorToaster from "../../../components/error/ErrorToaster";
import Loading from "../../../components/Loading";
import aimsTheme from "../../../styles/meridian-theme-aims.json";

import conciseIcon from "../../../images/verbosity_concise.svg";
import moderateIcon from "../../../images/verbosity_moderate.svg";
import detailedIcon from "../../../images/verbosity_detailed.svg";

const DETAIL_LEVEL_CARDS = [
  { key: VERBOSITY_LEVELS.CONCISE, label: "Concise", icon: conciseIcon },
  { key: VERBOSITY_LEVELS.MODERATE, label: "Moderate", icon: moderateIcon },
  { key: VERBOSITY_LEVELS.DETAILED, label: "Detailed", icon: detailedIcon },
];

const LEARN_MORE_URL = "https://w.amazon.com/bin/view/AmazonMeetings/MeetingSummaries/AiMS/DetailedSummaries/";

/**
 * SettingsTabInClient Component
 *
 * Displays the settings tab content for the client interface, including
 * verbosity level selector and FeedbackCard in a wider layout.
 */
const SettingsTabInClient = () => {
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [autoStartEnabled, setAutoStartEnabled] = useState(false);
  const [autoStartUpdating, setAutoStartUpdating] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);

  const showPreferenceSaveError = () =>
    setToasts((prev) => prev.concat({
      id: `preference-save-${++toastIdRef.current}`,
      message: PREFERENCE_SAVE_ERROR_MESSAGE,
    }));
  
  useEffect(() => {
    getVerbosityLevel().then(setSelectedLevel);
    getAutoStart().then(setAutoStartEnabled);
  }, []);

  const handleLevelChange = async (key) => {
    const previousLevel = selectedLevel;
    setSelectedLevel(key);
    try {
      await updateVerbosityLevel(key);
    } catch (error) {
      LogManager.error("Failed to update preference:", error);
      setSelectedLevel(previousLevel);
      showPreferenceSaveError();
    }
  };

  const handleLearnMoreClick = () => openUrl(LEARN_MORE_URL, "Failed to open Learn more URL:");

  if (!selectedLevel) return <Loading />;

  return (
    <Column spacing="none">
      <ErrorToaster
        toasts={toasts}
        onCloseToast={(id) => setToasts((prev) => prev.filter((t) => t.id !== id))}
      />
      <Row spacing="450" spacingInset="600 none none none" alignmentVertical="top">
        <Column width="304px" spacing="450" spacingInset="none none 450 none" alignmentHorizontal="start">
          {/* Meeting insights and summary */}
          <Column spacing="350">
            <Column spacing="200">
              <Text type="h100">Meeting insights and summary</Text>
              <Text type="b200" color="secondary">Turn these on for meetings you schedule</Text>
            </Column>
            <Theme palette={aimsTheme} tokens={{ toggleBackgroundColorChecked: THEME_COLOR.TEAL_GREEN }}>
              <Toggle
                checked={autoStartEnabled}
                disabled={autoStartUpdating}
                onChange={async () => {
                  const newValue = !autoStartEnabled;
                  setAutoStartEnabled(newValue);
                  setAutoStartUpdating(true);
                  try {
                    await updateAutoStart(newValue);
                    LogManager.metric(`Settings_AutoStart_${newValue ? "Enabled" : "Disabled"}`, 1, { source: "inClient" });
                  } catch (error) {
                    LogManager.error("Failed to update auto-start preference:", error);
                    setAutoStartEnabled(!newValue);
                    showPreferenceSaveError();
                  } finally {
                    setAutoStartUpdating(false);
                  }
                }}
              >
                <Text type="b200">Auto start</Text>
              </Toggle>
            </Theme>
          </Column>
          {/* Summary detail level */}
          <Column spacing="350">
            <Text type="h100">Summary detail level</Text>
            <Column spacing="300">
              <Row spacing="300" widths={["96px", "96px", "96px"]}>
                {DETAIL_LEVEL_CARDS.map(({ key, label, icon }) => {
                  const isSelected = selectedLevel === key;
                  return (
                    <Theme key={key} tokens={{ ...(isSelected && { boxBorderColorOutline: THEME_COLOR.TEAL_GREEN, cardBorderColor: THEME_COLOR.TEAL_GREEN }), radioButtonBackgroundColorSelectedDefault: THEME_COLOR.TEAL_GREEN, radioButtonBackgroundColorSelectedHover: THEME_COLOR.TEAL_GREEN, radioButtonDiameterSmall: 12, radioButtonIndicatorDiameterSmall: 5, radioButtonSpacingHorizontal: 8, radioButtonLabelSmallFontSize: 12, radioButtonLabelSmallLineHeight: "12px" }}>
                      <Card spacingInset="300" onClick={() => handleLevelChange(key)}>
                        <Column spacing="100" alignmentHorizontal="start">
                          <RadioButton checked={isSelected} name="verbosity" value={key} size="small" onChange={() => handleLevelChange(key)}>
                            <b>{label}</b>
                          </RadioButton>
                          <Row alignmentHorizontal="center" alignmentVertical="center" width="100%" height="36px">
                            <img src={icon} alt={label} />
                          </Row>
                        </Column>
                      </Card>
                    </Theme>
                  );
                })}
              </Row>
              <Link onClick={handleLearnMoreClick} type="secondary">
                <Theme tokens={{ textColorPrimary: THEME_COLOR.TEAL_GREEN }}>
                  <Text color="primary" type="b200"><u>Learn more</u></Text>
                </Theme>
              </Link>
            </Column>
          </Column>
        </Column>
        <InfoCard variant={INFO_CARD_VARIANT.FEEDBACK} page={FEEDBACK_PAGE.IN_CLIENT_FEEDBACK_TAB} />
      </Row>
    </Column>
  );
};

export default SettingsTabInClient;
